/**
 * Navigation Configuration
 *
 * Centralized source of truth for all navigation structures.
 * Used by: Header, MobileNav, BottomNav, Footer, CommandPalette
 *
 * SEO Optimized:
 * - Semantic link structure
 * - Clear hierarchy
 * - Descriptive labels
 * - Keyboard navigation support
 *
 * @example
 * ```tsx
 * import { NAVIGATION } from '@/lib/navigation/config';
 *
 * // Use in components
 * NAVIGATION.header.map(item => <NavLink item={item} />)
 * ```
 */

import {
  Home,
  User,
  FileText,
  Briefcase,
  Mail,
  Activity,
  Bookmark,
  Heart,
  Rss,
  Zap,
  Package,
  Boxes,
} from 'lucide-react';
import type { NavItem, NavSection, NavigationConfig } from './types';

// ============================================================================
// PRIMARY NAVIGATION (Header & Mobile)
// ============================================================================

/**
 * Core navigation items displayed in header
 * Optimized for discoverability and SEO
 */
export const HEADER_NAV: NavItem[] = [
  {
    href: '/',
    label: 'Home',
    shortLabel: 'Home',
    icon: Home,
    shortcut: 'g h',
    description: 'Return home',
    exactMatch: true,
  },
  {
    href: '/about',
    label: 'About',
    shortLabel: 'About',
    icon: User,
    shortcut: 'g a',
    description: 'Learn about us and our team',
  },
  {
    href: '/ai',
    label: 'AI',
    shortLabel: 'AI',
    icon: Zap,
    shortcut: 'g i',
    description: 'DCYFR AI, the open-source multi-provider agent framework',
    keywords: ['dcyfr ai', '@dcyfr/ai', 'agent', 'framework'],
  },
  {
    href: '/blog',
    label: 'Blog',
    shortLabel: 'Blog',
    icon: FileText,
    shortcut: 'g b',
    description: 'Read articles on security and development',
  },
  {
    href: '/code',
    label: 'Code',
    shortLabel: 'Code',
    icon: Package,
    shortcut: 'g o',
    description: 'npm packages, the component registry, and the plugin ecosystem',
    keywords: ['open source', 'packages', 'npm'],
  },
  {
    href: '/work',
    label: 'Work',
    shortLabel: 'Work',
    icon: Briefcase,
    shortcut: 'g w',
    description: 'Explore our portfolio and client work',
  },
];

/**
 * Call-to-action rendered as a button beside the search icon, outside the
 * main link list.
 */
export const HEADER_CTA: NavItem = {
  href: '/contact',
  label: 'Contact',
  shortLabel: 'Contact',
  icon: Mail,
  shortcut: 'g c',
  description: 'Get in touch with us',
};

// ============================================================================
// BLOG SUB-NAVIGATION
// ============================================================================

export const BLOG_NAV: NavItem[] = [
  {
    href: '/blog',
    label: 'All Posts',
    description: 'Browse our blog articles',
    exactMatch: true,
  },
  {
    href: '/blog/series',
    label: 'Blog Series',
    description: 'In-depth multi-part articles',
  },
  {
    href: '/blog?category=AI',
    label: 'Artificial Intelligence',
    description: 'Artificial intelligence and development',
  },
  {
    href: '/blog?category=Web',
    label: 'Web Development',
    description: 'Modern full-stack web development',
  },
  {
    href: '/blog?category=DevSecOps',
    label: 'DevSecOps',
    description: 'Cybersecurity and secure development',
  },
];

// ============================================================================
// WORK/PORTFOLIO SUB-NAVIGATION
// ============================================================================

export const WORK_NAV: NavItem[] = [
  {
    href: '/work',
    label: 'All Projects',
    description: 'View complete portfolio',
    exactMatch: true,
  },
  {
    href: '/work?category=code',
    label: 'Code Projects',
    description: 'Open-source code and development projects',
  },
  {
    href: '/work?category=startup',
    label: 'Startups',
    description: 'Startup ventures and product work',
  },
  {
    href: '/work?category=nonprofit',
    label: 'Nonprofits',
    description: 'Nonprofit and community contributions',
  },
];

// ============================================================================
// MOBILE NAVIGATION (Drawer)
// ============================================================================

/**
 * Mobile navigation sections with grouping
 * Optimized for touch targets and quick access
 */
export const MOBILE_NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    label: 'Main Navigation',
    description: 'Primary pages',
    items: [...HEADER_NAV, HEADER_CTA],
  },
  {
    id: 'discover',
    label: 'Discover',
    description: 'Additional content',
    items: [
      {
        href: '/registry',
        label: 'Registry',
        icon: Boxes,
        description: 'shadcn-compatible component registry',
      },
      {
        href: '/activity',
        label: 'Activity',
        icon: Activity,
        description: 'Recent updates',
      },
      {
        href: '/sponsors',
        label: 'Sponsors',
        icon: Heart,
        description: 'Support us',
      },
      {
        href: '/bookmarks',
        label: 'Bookmarks',
        icon: Bookmark,
        description: 'Your saved items',
      },
      { href: '/likes', label: 'Likes', icon: Heart, description: 'Content you liked' },
    ],
  },
];

// ============================================================================
// BOTTOM NAVIGATION (Mobile Only)
// ============================================================================

/**
 * Bottom navigation bar (mobile only)
 * Limited to 3-5 most important destinations
 */
export const BOTTOM_NAV: NavItem[] = [
  {
    href: '/',
    label: 'Home',
    icon: Home,
    exactMatch: true,
  },
  {
    href: '/ai',
    label: 'AI',
    icon: Zap,
  },
  {
    href: '/blog',
    label: 'Blog',
    icon: FileText,
  },
  {
    href: '/work',
    label: 'Work',
    icon: Briefcase,
  },
  {
    href: '/contact',
    label: 'Contact',
    icon: Mail,
  },
];

// ============================================================================
// FOOTER NAVIGATION
// ============================================================================

/**
 * Footer navigation sections
 * Comprehensive sitemap for SEO and discoverability
 */
export const FOOTER_NAV_SECTIONS: NavSection[] = [
  {
    id: 'explore',
    label: 'Explore',
    description: 'Secondary pages not in the header',
    items: [
      { href: '/services', label: 'Services', description: 'Work with DCYFR Labs' },
      { href: '/registry', label: 'Registry', description: 'shadcn-compatible component registry' },
      { href: '/docs', label: 'Docs', description: 'Package documentation' },
      { href: '/design', label: 'Design', description: 'The DCYFR design system' },
      { href: '/activity', label: 'Activity', description: 'Recent updates' },
      { href: '/feeds', label: 'Feeds', icon: Rss, description: 'Subscribe to updates' },
      { href: '/sponsors', label: 'Sponsors', description: 'Support our work' },
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    description: 'Policies and terms',
    items: [
      { href: '/privacy', label: 'Privacy', description: 'How we handle your data' },
      { href: '/terms', label: 'Terms', description: 'Usage terms' },
      {
        href: '/legal',
        label: 'Legal',
        description: 'Security, accessibility, acceptable use, and every other policy',
      },
    ],
  },
];

/** Flat footer link list, in section order (the chrome footer renders one row). */
export const FOOTER_NAV: NavItem[] = FOOTER_NAV_SECTIONS.flatMap((section) => section.items);

// ============================================================================
// DEV TOOLS NAVIGATION (Development Only)
// ============================================================================
// COMPLETE NAVIGATION CONFIG
// ============================================================================

/**
 * Complete navigation configuration
 * Single source of truth for all navigation structures
 */
export const NAVIGATION: NavigationConfig = {
  header: HEADER_NAV,
  cta: HEADER_CTA,
  mobile: MOBILE_NAV_SECTIONS,
  bottom: BOTTOM_NAV,
  footer: FOOTER_NAV_SECTIONS,
  megaMenus: {
    blog: [
      {
        id: 'categories',
        label: 'Categories',
        items: BLOG_NAV,
      },
    ],
    work: [
      {
        id: 'portfolio',
        label: 'Portfolio',
        items: WORK_NAV,
      },
    ],
  },
};

// ============================================================================
// UTILITY FUNCTIONS (Re-exported from utils.ts)
// ============================================================================

// All utility functions are implemented in utils.ts and re-exported here for convenience
export { isNavItemActive, getAriaCurrent, getNavAnalytics, formatShortcut } from './utils';

/**
 * Get all keyboard shortcuts from navigation
 * Useful for command palette and help UI
 */
export function getKeyboardShortcuts(): Array<{
  shortcut: string;
  label: string;
  href: string;
  description?: string;
}> {
  return [...HEADER_NAV, HEADER_CTA]
    .filter((item): item is NavItem & { shortcut: string } => !!item.shortcut)
    .map((item) => ({
      shortcut: item.shortcut,
      label: item.label,
      href: item.href,
      description: item.description,
    }));
}

/**
 * Find navigation item by href
 * Searches across all navigation structures
 */
export function findNavItem(href: string): NavItem | undefined {
  const allItems = [
    ...HEADER_NAV,
    HEADER_CTA,
    ...BLOG_NAV,
    ...WORK_NAV,
    ...BOTTOM_NAV,
    ...MOBILE_NAV_SECTIONS.flatMap((section) => section.items),
    ...FOOTER_NAV_SECTIONS.flatMap((section) => section.items),
  ];

  return allItems.find((item) => item.href === href);
}

/**
 * Command palette sections, deduplicated by href (first occurrence wins) so a
 * page linked from both the header and the footer is listed once.
 *
 * @param blogExtras - extra blog destinations (e.g. category filters) merged
 *   into the Blog section after BLOG_NAV
 */
export function getCommandNavSections(blogExtras: NavItem[] = []): NavSection[] {
  const seen = new Set<string>();
  const unique = (items: NavItem[]) =>
    items.filter((item) => {
      if (seen.has(item.href)) return false;
      seen.add(item.href);
      return true;
    });
  const discover = MOBILE_NAV_SECTIONS.find((section) => section.id === 'discover')?.items ?? [];

  return [
    { id: 'main', label: 'Navigation', items: unique([...HEADER_NAV, HEADER_CTA]) },
    { id: 'work', label: 'Work', items: unique(WORK_NAV) },
    { id: 'blog', label: 'Blog', items: unique([...BLOG_NAV, ...blogExtras]) },
    { id: 'site', label: 'Site', items: unique([...FOOTER_NAV, ...discover]) },
  ];
}
